import React, { createContext, useContext, useMemo, useState } from "react";
import "./ChatHistory.module.css";
import { ChatSender, SystemMessages } from "../../common/constants";
import {
  DEFAULT_TEXT,
  getUserConfigText,
  VIDEO_PROVIDER_MATCHERS,
  VIDEO_PROVIDER_URLS,
} from "../../config";
import { useEffect } from "react";
import { scrollHistoryToBottom } from "../Widget/utils";
import { useConfig } from "../Widget/ConfigContext";
import ReactPlayer from "react-player/lazy";
import { mapIconUrlToGCP } from "../../common/gcp";
import { calcContrastColor } from "../Widget/BubbleWidget";
import { calculateVoice, getSynthesisKey } from "../../common/langUtils";

const HistoryContext = createContext({
  synthKey: undefined,
  doSynth: () => {},
});

function useHistoryContext() {
  return useContext(HistoryContext);
}

function replaceWithLink(node, src) {
  const replacement = document.createElement("a");
  replacement.setAttribute("href", src);
  replacement.setAttribute("target", "_blank");
  replacement.classList.add("widget-link");
  replacement.appendChild(document.createTextNode(src));
  node.replaceWith(replacement);
}

// all markdown or html tags allowed to appear in messages
const allowedTags = ["a", "button", "em", "strong", "img", "audio", "video"];

/**
 * @param {Node} node
 */
function getSourceFromNode(node) {
  let src = null;

  // if the src property is on the parent node, return it
  if ((src = node.getAttribute("src"))) {
    return src;
  } else {
    Array.from(node.childNodes).forEach((child) => {
      if (src === null && child.tagName.toLowerCase() === "source") {
        src = child.getAttribute("src");
      }
    });
    return src;
  }
}

function SystemMessage({ text }) {
  const { config, colors } = useConfig();
  return (
    <p
      style={{
        paddingLeft: "0.5rem",
        paddingRight: "0.5rem",
        fontFamily: "sans-serif",
        fontSize: config.systemLabelFontSize,
        fontWeight: config.systemLabelFontWeight,
        color: colors.systemLabelText,
        textAlign: "center",
        width: "100%",
      }}
    >
      {text}
    </p>
  );
}

function BubbleWrapper({
  children,
  minimal,
  sender,
  copyMessage,
  speakMessage,
  stopMessage,
  showTts,
}) {
  const { config, colors } = useConfig();
  const { synthKey } = useHistoryContext();
  const [showCopyButton, setShowCopyButton] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const copyMessageText = useMemo(
    () =>
      getUserConfigText(
        config,
        "copyMessageText",
        DEFAULT_TEXT.en.copyMessageText,
      ),
    [document.cookie, config.overrideLanguage],
  );

  const bubbleColors = useMemo(
    () => ({
      bot: colors.botMessageBubble,
      human: colors.userMessageBubble,
      human_agent: colors.humanAgentMessageBubble,
    }),
    [colors],
  );

  const textColors = useMemo(
    () => ({
      bot: colors.botMessageText,
      human: colors.userMessageText,
      human_agent: colors.humanAgentMessageText,
    }),
    [colors],
  );

  function handleMouseEnter() {
    if (config.showCopyButtons) {
      setShowCopyButton(true);
    }
  }

  function handleMouseLeave() {
    if (config.showCopyButtons) {
      setShowCopyButton(false);
      setHasCopied(false);
    }
  }

  function handleCopyClick() {
    setHasCopied(true);
    copyMessage();
  }

  function handleSpeakClick() {
    setIsSpeaking(true);
    speakMessage(() => {
      setIsSpeaking(false);
    });
  }

  function handleStopSpeakClick() {
    speechSynthesis.cancel();
    setIsSpeaking(false);
  }

  return (
    <>
      {!minimal ? (
        <>
          <div
            dir="auto"
            style={{
              // magicform-message-bubble
              margin: "0.25rem 0.25rem",
              padding: "1rem 1rem",
              borderRadius: "8px",
              boxSizing: "border-box",
              maxWidth: "80%",
              overflowAnchor: "none",
              lineHeight: "1.5",
              // align right for human, left for all other
              alignSelf:
                sender === ChatSender.human ? "flex-end " : "flex-start",
              backgroundColor: bubbleColors[sender],
              position: "relative",
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              style={
                showCopyButton
                  ? {
                      position: "absolute",
                      display: "block",
                      top: "-10px",
                      padding: "0px",
                      margin: "0px",
                      backgroundColor:
                        sender === ChatSender.bot
                          ? colors.botMessageBubble
                          : colors.userMessageBubble,
                      filter: "brightness(120%)",
                      boxShadow: "0 5px 10px rgb(0 0 0 / 0.25)",
                      border: "none",
                      borderRadius: "200px",
                      width: "25px",
                      height: "25px",
                      ...(sender === ChatSender.human
                        ? {
                            left: "-10px",
                          }
                        : { right: "-10px" }),
                    }
                  : { display: "none" }
              }
              onClick={() => handleCopyClick()}
              title={copyMessageText}
            >
              {hasCopied ? (
                <img
                  src={mapIconUrlToGCP(config.checkIconUrl)}
                  alt="Check icon"
                  className="check-fade-in"
                  style={{
                    margin: "0",
                    padding: "5px",
                    width: "100%",
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: "100%",
                    filter:
                      calcContrastColor(colors.botMessageBubble) === "white"
                        ? "invert(100%)"
                        : "",
                  }}
                />
              ) : (
                <img
                  src={mapIconUrlToGCP(config.copyIconUrl)}
                  alt="clipboard icon"
                  style={{
                    margin: "0",
                    padding: "5px",
                    width: "100%",
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: "100%",
                    filter:
                      calcContrastColor(colors.botMessageBubble) === "white"
                        ? "invert(100%)"
                        : "",
                  }}
                />
              )}
            </button>
            <div
              style={{
                fontSize: config.messageFontSize,
                color: textColors[sender],
                fontFamily: "sans-serif",
                margin: "0",
                overflowWrap: "break-word",
                position: "static",
              }}
            >
              {children}
            </div>
            {showTts !== false &&
              synthKey &&
              config?.showTtsButtons &&
              sender !== ChatSender.human && (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-start",
                    marginBottom: "-0.5rem",
                    marginLeft: "-0.5rem",
                  }}
                >
                  {isSpeaking ? (
                    <>
                      <button
                        onClick={() => handleStopSpeakClick()}
                        aria-label="stop currently playing message"
                        className="magicform-speaker-button"
                        style={{
                          height: "28px",
                          width: "28px",
                          marginTop: "5px",
                          border: "none",
                          borderRadius: "2rem",
                        }}
                      >
                        <img
                          src={mapIconUrlToGCP(config.stopIconUrl)}
                          alt="stop symbol"
                          style={{
                            width: "15px",
                            height: "15px",
                            filter:
                              calcContrastColor(colors.botMessageBubble) ===
                              "white"
                                ? "invert(100%)"
                                : "",
                          }}
                        />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleSpeakClick()}
                      className="magicform-speaker-button"
                      aria-label="play message via audio transcription"
                      style={{
                        height: "28px",
                        width: "28px",
                        marginTop: "5px",
                        border: "none",
                        borderRadius: "2rem",
                      }}
                    >
                      <img
                        src={mapIconUrlToGCP(config.speakerIconUrl)}
                        alt="speaker icon"
                        style={{
                          width: "15px",
                          height: "15px",
                          filter:
                            calcContrastColor(colors.botMessageBubble) ===
                            "white"
                              ? "invert(100%)"
                              : "",
                        }}
                      />
                    </button>
                  )}
                </div>
              )}
          </div>
        </>
      ) : (
        <div
          // sets which side of the chat window the bubble should align to,
          // human on right, bot or any other on left
          dir="auto"
          style={{
            margin: "0.25rem 0.25rem",
            padding: "1rem 1rem",
            borderRadius: "8px",
            borderBottomRightRadius: "0",
            border: "1px solid lightgray",
            boxSizing: "border-box",
            maxWidth: "100%",
            overflowAnchor: "none",
            lineHeight: "1.5",
            cursor: "pointer",
            backgroundColor: bubbleColors[sender],
            alignSelf: "flex-end", // when minimal, should align to right = flex-end
          }}
        >
          <div
            style={{
              color: textColors[sender],
            }}
          >
            {children}
          </div>
        </div>
      )}
    </>
  );
}

function MessageBubble({ message, sender, minimal, toggleChatOpen }) {
  const { config } = useConfig();
  const { doSynth } = useHistoryContext();
  message = message.trim();

  const approvedVideoProviderUrls = useMemo(() => {
    let approvedUrls = [];
    Object.entries(config.videoProviders).forEach(([key, settingObj]) => {
      if (settingObj.enabled && key !== "facebook") {
        approvedUrls.push(VIDEO_PROVIDER_URLS[key]);
      }
    });
    return approvedUrls;
  }, [config]);

  const approvedVideoProviderMatchers = useMemo(() => {
    let matchers = [];
    Object.entries(config.videoProviders).forEach(([key, settingObj]) => {
      if (settingObj.enabled && VIDEO_PROVIDER_MATCHERS[key]) {
        matchers.push(VIDEO_PROVIDER_MATCHERS[key]);
      }
    });
    return matchers;
  }, [config]);

  function isValidImageUrl(testUrl) {
    if (config.approvedImageFormats.length < 1) {
      return false;
    } else {
      // if there are some approved image formats, validate the url. If valid,
      // check if it ends with one of the extensions. If it does, we can render
      // the image
      try {
        const url = new URL(testUrl);
        const valid = config.approvedImageFormats
          .split(",")
          .some((format) => url.toString().endsWith("." + format));
        return valid;
      } catch (_) {
        return false;
      }
    }
  }

  function isValidAudioUrl(testUrl) {
    if (config.approvedAudioFormats.length < 1) {
      return false;
    } else {
      // if there are some approved audio formats, validate the url. If valid,
      // check if it ends with one of the extensions. If it does, we can render
      // the file in a player
      try {
        const url = new URL(testUrl);
        const valid = config.approvedAudioFormats
          .split(",")
          .some((format) => url.toString().endsWith("." + format));
        return valid;
      } catch (_) {
        return false;
      }
    }
  }

  function isValidVideoUrl(testUrl) {
    if (
      config.approvedVideoFormats.length < 1 &&
      approvedVideoProviderUrls.length < 1 &&
      approvedVideoProviderMatchers.length < 1
    ) {
      return false;
    } else {
      // if there are some approved video formats, validate the url. If valid,
      // check if it ends with one of the extensions or starts with one of the
      // provider urls. If so, we can render the link in a player
      try {
        const url = new URL(testUrl);
        const valid =
          config.approvedVideoFormats
            .split(",")
            .some((format) => url.toString().endsWith("." + format)) ||
          approvedVideoProviderUrls.some((providerUrl) =>
            url.toString().startsWith(providerUrl),
          ) ||
          approvedVideoProviderMatchers.some(
            (matcher) => url.toString().match(matcher)[0] !== null,
          );
        return valid;
      } catch (_) {
        return false;
      }
    }
  }

  /**
   * Function to recursively sanitize the content
   *
   * @param {HTMLElement} node
   */
  function sanitizeNode(node) {
    // If the node is an element node
    if (node.nodeType === Node.ELEMENT_NODE) {
      // If the element's tag name is not allowed, remove it from the DOM
      if (!allowedTags.includes(node.tagName.toLowerCase())) {
        node.replaceWith(...node.childNodes); // Replace the element with its children
      } else {
        const tagName = node.tagName.toLowerCase();

        // if node is an image tag
        if (tagName === "img") {
          const src = node.getAttribute("src");

          // replace the image with a link if it's not an approved extension,
          // or removes it if the src is null
          if (src === null) {
            node.remove();
          } else if (!isValidImageUrl(src)) {
            replaceWithLink(node, src);
          }
        }

        // if node is an audio tag
        if (tagName === "audio") {
          const src = getSourceFromNode(node);

          // after we have the source, if it's null remove the node entirely.
          // If it's a string but not an acceptable url based on the config,
          // replace it with a link. Otherwise, remove it.

          if (src === null) {
            node.remove();
          } else if (!isValidAudioUrl(src)) {
            // replace the audio with a link if it's not an approved extension
            replaceWithLink(node, src);
          } else {
            // if the audio is an approved extension, create a uniform audio
            // element so it's easier to process
            const replacement = document.createElement("audio");
            replacement.setAttribute("src", src);
            node.replaceWith(replacement);
          }
        }

        // if node is a video tag
        if (tagName === "video") {
          const src = getSourceFromNode(node);

          // after we have the source, if it's null remove the node entirely.
          // If it's a string but not an acceptable url based on the config,
          // replace it with a link. Otherwise, remove it.

          if (src === null) {
            node.remove();
          } else if (!isValidVideoUrl(src)) {
            // replace the video with a link if it's not an approved extension
            replaceWithLink(node, src);
          } else {
            // if the audio is an approved extension, create a uniform audio
            // element so it's easier to process
            const replacement = document.createElement("video");
            replacement.setAttribute("src", src);
            node.replaceWith(replacement);
          }
        }

        // also check links to see if they match rich media settings. If yes,
        // replace them with the corresponding elements
        if (tagName === "a") {
          let src;

          if ((src = node.getAttribute("href"))) {
            // image
            if (isValidImageUrl(src)) {
              const replacement = document.createElement("img");
              replacement.setAttribute("src", src);
              node.replaceWith(replacement);
            } else if (isValidAudioUrl(src)) {
              // audio
              const replacement = document.createElement("audio");
              replacement.setAttribute("src", src);
              node.replaceWith(replacement);
            } else if (isValidVideoUrl(src)) {
              // video
              const replacement = document.createElement("video");
              replacement.setAttribute("src", src);
              node.replaceWith(replacement);
            } else {
              // if it's just a normal link, set to open in new tab and add
              // styling
              node.setAttribute("target", "_blank");
              node.classList.add("widget-link");
            }
          }
        }

        // If the element is allowed, recursively sanitize its children
        Array.from(node.childNodes).forEach(sanitizeNode);
      }
    }
  }

  function sanitizeHtml(inputHtml) {
    // Create a new DOMParser instance
    const parser = new DOMParser();

    // Parse the input HTML string into a document
    const doc = parser.parseFromString(inputHtml, "text/html");

    // Start the sanitization process from the body element of the document
    Array.from(doc.body.childNodes).forEach(sanitizeNode);

    // return the sanitized HTML as a tree
    return doc.body;
  }

  /**
   * @param {HTMLElement} sanitizedHtml
   */
  function createContents(sanitizedHtml) {
    // after html has been sanitized into a relatively flat tree of only
    // approved elements, render it as jsx elements. These will be wrapped in a
    // message bubble and presented as a single message

    const childNodes = Array.from(sanitizedHtml.childNodes);

    return childNodes.map((node, index) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent?.trim() === "") {
          return null;
        }

        return <span key={index}>{node.textContent}</span>;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toLowerCase();
        const src = node.getAttribute("src");

        switch (tag) {
          case "img":
            return (
              <img
                key={index}
                src={src}
                onClick={() => window.open(src, "_blank")}
                style={{
                  display: "block",
                  maxWidth: "100%",
                  height: "auto",
                  cursor: "pointer",
                }}
              ></img>
            );
          case "audio":
            return (
              <audio
                key={index}
                controls
                src={src}
                style={{
                  width: "2000px",
                  maxWidth: "100%",
                }}
              ></audio>
            );
          case "video":
            return (
              <ReactPlayer
                key={index}
                url={src}
                height={"auto"}
                width="20000px"
                controls
                style={{
                  height: "auto",
                  aspectRatio: "16 / 9",
                  maxWidth: "100%",
                }}
              ></ReactPlayer>
            );

          default:
            // for tags we're not handling specially, just render the contents
            // (for cases like text formatting)
            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: node.outerHTML }}
              ></span>
            );
        }
      }

      return null;
    });
  }

  function copyMessage() {
    navigator.clipboard.writeText(message);
  }

  function speakMessage(onEnd) {
    // convert message to text only by parsing it into a document and converting
    // non-text nodes to text
    const parser = new DOMParser();
    const doc = parser.parseFromString(message, "text/html");
    Array.from(doc.body.childNodes).forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE) {
        const tagName = node.tagName.toLowerCase();
        switch (tagName) {
          case "img":
            node.replaceWith("inline image");
            break;
          case "video":
            node.replaceWith("inline video player");
            break;
          case "audio":
            node.replaceWith("inline audio player");
            break;
          case "strong":
            node.replaceWith(...node.childNodes);
            break;
          case "a":
            const href = node.getAttribute("href");
            if (href) {
              const host = new URL(href);
              node.replaceWith(`link to ${host.hostname}`);
            } else {
              node.replaceWith("link");
            }
            break;
          default:
            if (
              Array.from(node.childNodes).some(
                (child) => child.nodeType === Node.TEXT_NODE,
              )
            ) {
              node.replaceWith(node.childNodes);
            }
        }
      }
    });

    doSynth(doc.body.innerHTML, onEnd);
  }

  // sanitized html and resulting display contents will not change unless the
  // message contents changes
  const messageContents = useMemo(() => {
    const sanitized = sanitizeHtml(message);
    return createContents(sanitized);
  }, [message]);

  // render system messages as inline text akin to disclaimers
  if (sender === ChatSender.system) {
    if (message in SystemMessages) {
      const text = getUserConfigText(
        config,
        SystemMessages[message], // map system message to text key
        DEFAULT_TEXT.en[SystemMessages[message]],
      );

      // if the user has text configured for the system message key, render it,
      // otherwise render nothing for this message
      if (text) {
        return <SystemMessage text={text} />;
      } else return;
    }
  } else {
    return (
      messageContents.length > 0 && (
        <>
          <BubbleWrapper
            minimal={minimal}
            sender={sender}
            copyMessage={copyMessage}
            speakMessage={speakMessage}
          >
            {messageContents}
          </BubbleWrapper>
        </>
      )
    );
  }
}

function TypingIndicator() {
  const { colors } = useConfig();
  const textColor = colors.botMessageText;

  const [dotCount, setDotCount] = useState(1);
  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prevDotCount) => {
        return prevDotCount === 5 ? 1 : prevDotCount + 1;
      });
    }, 400);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const dots = Array(dotCount).fill(".");

  return <span style={{ color: textColor }}>{dots}</span>;
}

/**
 * @typedef {import('../../types/config').ConfigObject} ConfigObject
 */
/**
 *
 * @param {{
 *   messages: any,
 *   thinking: any,
 *   minimal: any,
 *   toggleChatOpen: any,
 *   hideChat: any
 * }} param0
 * @returns
 */
export default function ChatHistory({
  messages,
  thinking,
  minimal,
  toggleChatOpen,
  hideChat,
}) {
  const { config } = useConfig();

  const histDiscText = useMemo(
    () =>
      getUserConfigText(
        config,
        "historyDisclaimer",
        DEFAULT_TEXT.en.historyDisclaimer,
      ),
    [document.cookie],
  );

  // setup speechSynthesis
  const [synthKey, setSynthKey] = useState(undefined);
  const [voice, setVoice] = useState(undefined);

  function updateSynthesisKey() {
    const synthLangKey = getSynthesisKey(config);

    if (synthLangKey === false) {
      setSynthKey(undefined);
    } else {
      setSynthKey(synthLangKey);
    }
  }

  function doSynth(message, onEnd) {
    if (synthKey) {
      const utterance = new SpeechSynthesisUtterance(message);
      utterance.lang = synthKey;

      // if for some reason we couldn't pick a voice, default to the system's
      // voice for the given language
      if (voice) {
        utterance.voice = voice;
      }

      if (onEnd) {
        utterance.onend = onEnd;
      }

      speechSynthesis.speak(utterance);
    }
  }

  // when the synthesis key changes, calculate the new best voice from the external
  // list
  useEffect(() => {
    if (synthKey) {
      (async () => {
        const calculatedVoice = await calculateVoice(synthKey);
        setVoice(calculatedVoice ?? undefined);
      })();
    }
  }, [synthKey]);

  // update synthesis once when voices are loaded
  useEffect(() => {
    // only set voice synthesis if tts buttons are enabled
    if (config.showTtsButtons) {
      speechSynthesis.onvoiceschanged = () => {
        updateSynthesisKey();

        speechSynthesis.onvoiceschanged = undefined;
      };
    }
  }, []);

  useEffect(() => {
    if (config.showTtsButtons) {
      updateSynthesisKey();
    }
  }, [document.cookie, config?.overrideLanguage]);

  useEffect(() => {
    scrollHistoryToBottom();
  }, [messages, thinking]);

  const onCrossClick = () => {
    if (hideChat) {
      hideChat();
    }
  };

  return (
    <HistoryContext.Provider value={{ synthKey, doSynth }}>
      <div
        className={
          "magicform-chat-history-minimal " +
          (minimal
            ? "magicform-chat-history-hide-scrollbar"
            : "magicform-chat-history-show-scrollbar")
        }
      >
        {histDiscText && (
          <p
            dir="auto"
            style={{
              paddingLeft: "0.5rem",
              paddingRight: "0.5rem",
              fontFamily: "sans-serif",
              fontSize: config.historyDiscFontSize,
              fontWeight: config.historyDiscFontWeight,
              color: "gray",
              textAlign: "center",
              width: "100%",
            }}
          >
            {histDiscText}
          </p>
        )}
        {messages.length && minimal ? (
          <div onClick={onCrossClick} className="magicform-delete-button">
            &times;
          </div>
        ) : (
          <></>
        )}
        {messages.map((message, index) => (
          <MessageBubble
            key={index}
            message={message.text}
            sender={message.sender}
            minimal={minimal}
            toggleChatOpen={toggleChatOpen}
          />
        ))}
        {thinking && (
          <BubbleWrapper
            minimal={minimal}
            sender={ChatSender.bot}
            showTts={false}
          >
            <TypingIndicator />
          </BubbleWrapper>
        )}
        <div className="magicform-scroll-anchor"></div>
      </div>
    </HistoryContext.Provider>
  );
}
